import { useEffect, useRef, useState } from 'react';
import { ListChecks } from 'lucide-react';
import { SECTION_LABEL } from '../taskTheme';

/** The shell every editable text field in the head shares. */
const FIELD = 'w-full rounded-md border border-transparent bg-transparent px-1.5 outline-none hover:bg-[#FBFBFB] focus:border-[rgba(0,0,0,0.14)] focus:bg-white focus:shadow-[0_0_0_3px_rgba(0,0,0,0.03)]';

interface TaskDetailHeadProps {
  taskKey: string;
  title: string;
  description: string | null;
  disabled?: boolean;
  onTitleChange: (title: string) => void;
  onDescriptionChange: (description: string | null) => void;
}

/**
 * The task's identity: its key, its title and its description.
 *
 * Both fields commit on blur rather than on every keystroke, so one edit is one
 * write. An empty title is refused because the column requires one — Escape
 * puts back what was there rather than leaving a field that cannot save.
 */
export const TaskDetailHead = ({
  taskKey, title, description, disabled = false, onTitleChange, onDescriptionChange,
}: TaskDetailHeadProps) => {
  const [ draftTitle, setDraftTitle ] = useState(title);
  const [ draftDescription, setDraftDescription ] = useState(description ?? '');
  const descriptionRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => setDraftTitle(title), [ title ]);
  useEffect(() => setDraftDescription(description ?? ''), [ description ]);

  // Grown to its content rather than scrolled: a description is read far more
  // often than it is written, and a four-line box with a scrollbar hides most
  // of a long one.
  useEffect(() => {
    const field = descriptionRef.current;

    if (field) {
      field.style.height = 'auto';
      field.style.height = `${field.scrollHeight}px`;
    }
  }, [ draftDescription ]);

  const commitTitle = () => {
    const next = draftTitle.trim();

    if (!next) {
      setDraftTitle(title);

      return;
    }

    if (next !== title) {
      onTitleChange(next);
    }
  };

  const commitDescription = () => {
    const next = draftDescription.trim();

    if (next !== (description ?? '')) {
      onDescriptionChange(next || null);
    }
  };

  return (
    <div>
      <div className="inline-flex items-center gap-1.5 rounded-[5px] bg-[#F1F0EC] px-[9px] py-1">
        <ListChecks className="size-4 shrink-0 text-[#9A9A9A]" aria-hidden />
        <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[#9A9A9A]">
          {taskKey}
        </span>
      </div>

      <input
        value={draftTitle}
        disabled={disabled}
        placeholder="Untitled"
        aria-label="Task title"
        className={`${FIELD} mt-1.5 font-fraunces text-base font-semibold tracking-[-0.02em] text-[#181818]`}
        onChange={(event) => setDraftTitle(event.target.value)}
        onBlur={commitTitle}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.currentTarget.blur();
          }

          if (event.key === 'Escape') {
            setDraftTitle(title);
          }
        }}
      />

      <div className="mt-3.5">
        <div className={`${SECTION_LABEL} mb-1.5`}>Description</div>
        <textarea
          ref={descriptionRef}
          value={draftDescription}
          disabled={disabled}
          rows={1}
          placeholder="Add a description"
          aria-label="Task description"
          className={`${FIELD} resize-none py-0.5 text-[13px] leading-[1.55] text-[#696969]`}
          onChange={(event) => setDraftDescription(event.target.value)}
          onBlur={commitDescription}
        />
      </div>
    </div>
  );
};
